import Container from 'components/molecules/Container/Container';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import {
  useEnglishProficiencyAssessmentQuery,
  useSubmitEnglishProficiencyAssessmentMutation,
} from 'generated/graphql';
import { useDisableCopyAndRightClick } from 'hooks/useDisableCopyAndRightClick';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InlineNotification,
  Loader,
  Modal,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import EnglishProficiencyForm from './EnglishProficiencyForm';
import EnglishProficiencyTimer from './EnglishProficiencyTimer';
import {
  countAnsweredQuestions,
  seedAnswers,
  TEnglishProficiencyAnswer,
  TEnglishProficiencyAnswers,
} from './EnglishProficiencyTest.helpers';
import { useEnglishProficiencyResponses } from './useEnglishProficiencyResponses';
import styles from './EnglishProficiencyTest.module.scss';

/** How far the server's clock has to differ from ours before the countdown is
 * pulled back into line. Below this, correcting it would only make the number
 * jump for no reason. */
const CLOCK_DRIFT_TOLERANCE_MS = 2_000;

interface IEnglishProficiencyTest {
  /** The qcalc sitting, from `startEnglishProficiencyAssessment`. */
  assessmentId: string;
  onFinishTest: () => void;
}

/**
 * The sitting itself: all sixty items on one page, under one clock.
 *
 * One page rather than one question at a time because the form is fixed and
 * the server takes answers in any order — so there is nothing to decide
 * between questions, and a candidate who wants to go back and change an answer
 * only has to scroll.
 *
 * Answers are written as they are made, not gathered up and posted at the end.
 * An hour is long enough that a closed laptop is an ordinary way for a sitting
 * to end, and everything saved still counts.
 */
const EnglishProficiencyTest: FC<IEnglishProficiencyTest> = ({
  assessmentId,
  onFinishTest,
}) => {
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  useDisableCopyAndRightClick();

  const [answers, setAnswers] = useState<TEnglishProficiencyAnswers>({});
  // The same answers, readable synchronously. Blur can land in the same tick
  // as the keystroke that completed the answer, and state would still be one
  // character behind; it also keeps the handlers stable, which matters when
  // every keystroke would otherwise re-render sixty cards.
  const answersRef = useRef<TEnglishProficiencyAnswers>({});
  const [deadlineAtMs, setDeadlineAtMs] = useState<number | null>(null);
  const [isConfirmingSubmit, setIsConfirmingSubmit] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  // A sitting can end three ways — the candidate submits, the last answer
  // lands, the clock runs out — and they race each other in exactly the
  // moments they are most likely to happen. Whichever gets here first closes
  // the page; the others find the door already shut.
  const isFinishingRef = useRef(false);

  const [submitAssessment] = useSubmitEnglishProficiencyAssessmentMutation();

  const queryResponse = useEnglishProficiencyAssessmentQuery({
    variables: { id: assessmentId },
    fetchPolicy: 'network-only',
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
  });
  const assessment = queryResponse.data?.englishProficiencyAssessment;

  // Seed from what the server already holds, so a candidate returning to a
  // sitting finds their answers where they left them.
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!assessment || seededForRef.current === assessment.id) {
      return;
    }
    seededForRef.current = assessment.id;
    answersRef.current = seedAnswers(assessment.responses);
    setAnswers(answersRef.current);
    setDeadlineAtMs(Date.now() + assessment.remainingSeconds * 1000);
  }, [assessment]);

  /** Claims the close, or reports that someone else already has. */
  const claimFinish = useCallback((): boolean => {
    if (isFinishingRef.current) {
      return false;
    }
    isFinishingRef.current = true;
    setIsFinishing(true);
    return true;
  }, []);

  /** The sitting is already over on the server — leave, with nothing to send. */
  const finishWithoutSubmitting = useCallback(() => {
    if (!claimFinish()) {
      return;
    }
    onFinishTest();
  }, [claimFinish, onFinishTest]);

  const handleRemainingSeconds = useCallback((remainingSeconds: number) => {
    const serverDeadline = Date.now() + remainingSeconds * 1000;
    setDeadlineAtMs(current =>
      current == null ||
      Math.abs(current - serverDeadline) > CLOCK_DRIFT_TOLERANCE_MS
        ? serverDeadline
        : current
    );
  }, []);

  const handleRejected = useCallback(
    (message: string) => {
      handleMsgType({ type: TNotification.error, message });
    },
    [handleMsgType]
  );

  const { save, pendingCount, isOffline, flush } =
    useEnglishProficiencyResponses({
      assessmentId,
      onRemainingSeconds: handleRemainingSeconds,
      onRejected: handleRejected,
    });

  /** Flush whatever is queued, then close the sitting and leave. */
  const submitAndFinish = useCallback(async () => {
    if (!claimFinish()) {
      return;
    }
    // An answer chosen in the last moments is still crossing the network; it
    // has to land before the sitting is closed against it.
    await flush();
    try {
      await submitAssessment({ variables: { id: assessmentId } });
    } catch (_error) {
      // Finalising is idempotent, and the clock or the sixtieth answer may
      // have closed the sitting already. Either way the candidate is done.
    }
    onFinishTest();
  }, [assessmentId, claimFinish, flush, onFinishTest, submitAssessment]);

  const handleExpire = useCallback(() => {
    void submitAndFinish();
  }, [submitAndFinish]);

  const rememberAnswer = useCallback(
    (questionIndex: number, answer: TEnglishProficiencyAnswer) => {
      answersRef.current = { ...answersRef.current, [questionIndex]: answer };
      setAnswers(answersRef.current);
    },
    []
  );

  const handleChooseAnswer = useCallback(
    (questionIndex: number, selectedAnswerIndex: number) => {
      rememberAnswer(questionIndex, {
        selectedAnswerIndex,
        blankAnswers: null,
      });
      // A chosen option is a finished thought — no reason to wait.
      save(
        questionIndex,
        { selectedAnswerIndex, blankAnswers: null },
        { immediate: true }
      );
    },
    [rememberAnswer, save]
  );

  const handleTypeBlanks = useCallback(
    (questionIndex: number, blankAnswers: string[]) => {
      rememberAnswer(questionIndex, {
        selectedAnswerIndex: null,
        blankAnswers,
      });
      // Only once every gap is filled. Grading is all-or-nothing, so a
      // part-filled answer is a wrong one either way, and this keeps a word
      // being spelled out from being sent a letter at a time.
      if (blankAnswers.every(blankAnswer => blankAnswer.trim().length > 0)) {
        save(questionIndex, { selectedAnswerIndex: null, blankAnswers });
      }
    },
    [rememberAnswer, save]
  );

  const handleBlurBlanks = useCallback(
    (questionIndex: number) => {
      const blankAnswers = answersRef.current[questionIndex]?.blankAnswers;
      if (
        blankAnswers?.length &&
        blankAnswers.every(blankAnswer => blankAnswer.trim().length > 0)
      ) {
        save(
          questionIndex,
          { selectedAnswerIndex: null, blankAnswers },
          { immediate: true }
        );
      }
    },
    [save]
  );

  const questions = useMemo(() => assessment?.questions ?? [], [assessment]);
  const answeredCount = useMemo(
    () => countAnsweredQuestions(questions, answers),
    [questions, answers]
  );
  const unansweredCount = questions.length - answeredCount;

  // A sitting the server has already settled — an expired clock finalised on
  // read, or a second tab that finished it. There is nothing to sit.
  useEffect(() => {
    if (assessment?.submittedAt) {
      finishWithoutSubmitting();
    }
  }, [assessment?.submittedAt, finishWithoutSubmitting]);

  if (queryResponse.loading || !assessment || deadlineAtMs == null) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  return (
    <>
      <HelmetAndPageAnnouncer
        pageTitle={t('englishProficiencyTest.englishProficiencyTest')}
      />
      <EnglishProficiencyForm
        questions={questions}
        answers={answers}
        onChooseAnswer={handleChooseAnswer}
        onTypeBlanks={handleTypeBlanks}
        onBlurBlanks={handleBlurBlanks}
        disabled={isFinishing}
        lead={
          <EnglishProficiencyTimer
            deadlineAtMs={deadlineAtMs}
            onExpire={handleExpire}
          />
        }
        submitLabel={t('englishProficiencyTest.submit')}
        onSubmit={() => setIsConfirmingSubmit(true)}
        notice={
          isOffline ? (
            <InlineNotification
              notificationType={NotificationType.WARNING}
              className={styles.notification}
            >
              {t('englishProficiencyTest.offline', { count: pendingCount })}
            </InlineNotification>
          ) : null
        }
      />

      <Modal
        isOpen={isConfirmingSubmit}
        onClose={() => setIsConfirmingSubmit(false)}
        className={styles.confirmModal}
      >
        <h2 className={styles.confirmTitle}>
          {t('englishProficiencyTest.confirmSubmitTitle')}
        </h2>
        <p className={styles.confirmText}>
          {unansweredCount > 0
            ? t('englishProficiencyTest.confirmSubmitText', {
                count: unansweredCount,
              })
            : t('englishProficiencyTest.confirmSubmitTextAllAnswered')}
        </p>
        <div className={styles.confirmButtons}>
          <Button
            variant="secondary"
            onClick={() => setIsConfirmingSubmit(false)}
          >
            {t('englishProficiencyTest.keepAnswering')}
          </Button>
          <Button
            onClick={() => {
              setIsConfirmingSubmit(false);
              void submitAndFinish();
            }}
            disabled={isFinishing}
          >
            {t('englishProficiencyTest.submit')}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default EnglishProficiencyTest;
